import { useState, useRef, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import FirmsPage, { FIRMS, FirmDetailRoute } from './FirmsPage';
import { getFirmVisitHref } from './firmsData';
import Quiz from './Quiz';
import ContactPage from './ContactPage';
import styles from './App.module.css';

/** Bump with `public/index.html` ?v= when regenerating favicons / touch icon. */
const BRAND_MARK_VERSION = '17';
const brandMarkUrl = `${process.env.PUBLIC_URL || ''}/apple-touch-icon.png?v=${BRAND_MARK_VERSION}`;

const TOP_RATED_FIRMS = [
  { firmId: "Topstep", name: "Topstep", website: "topstep.com", logo: "https://www.google.com/s2/favicons?domain=topstep.com&sz=64", trustpilot: 3.4 },
  { firmId: "apex", name: "Apex Trader Funding", website: "apextraderfunding.com", logo: "https://www.google.com/s2/favicons?domain=apextraderfunding.com&sz=64", trustpilot: 4.4 },
  { firmId: "alpha", name: "Alpha Futures", website: "alpha-futures.com", logo: "https://www.google.com/s2/favicons?domain=alpha-futures.com&sz=64", trustpilot: 4.9 },
  { firmId: "mffu", name: "MyFundedFutures", website: "myfundedfutures.com", logo: "https://www.google.com/s2/favicons?domain=myfundedfutures.com&sz=64", trustpilot: 4.9 },
  { firmId: "tradeify", name: "Tradeify", website: "tradeify.co", logo: "https://www.google.com/s2/favicons?domain=tradeify.co&sz=64", trustpilot: 4.6 },
  { firmId: "lucid", name: "Lucid Trading", website: "lucidtrading.com", logo: "https://www.google.com/s2/favicons?domain=lucidtrading.com&sz=64", trustpilot: 4.7 },
  { firmId: "tpt", name: "Take Profit Trader", website: "takeprofittrader.com", logo: "https://www.google.com/s2/favicons?domain=takeprofittrader.com&sz=64", trustpilot: 4.4 },
  { firmId: "fundednext", name: "FundedNext Futures", website: "fundednext.com", logo: "https://www.google.com/s2/favicons?domain=fundednext.com&sz=64", trustpilot: 4.5 },
  { firmId: "topone", name: "Top One Futures", website: "toponefutures.com", logo: "https://www.google.com/s2/favicons?domain=toponefutures.com&sz=64", trustpilot: 4.8 },
  { firmId: "tradeday", name: "TradeDay", website: "tradeday.com", logo: "https://www.google.com/s2/favicons?domain=tradeday.com&sz=64", trustpilot: 4.6 },
  { firmId: "earn2trade", name: "Earn2Trade", website: "earn2trade.com", logo: "https://www.google.com/s2/favicons?domain=earn2trade.com&sz=64", trustpilot: 4.7 },
  { firmId: "e8futures", name: "E8 Futures", website: "e8markets.com", logo: "https://www.google.com/s2/favicons?domain=e8funding.com&sz=64", trustpilot: 4.5 },
];

const EDUCATION_SECTIONS = [
  {
    id: 'opportunity',
    number: '01',
    title: 'The opportunity prop firms give traders',
    paragraphs: [
      { text: 'Prop firms solve the biggest problem retail traders face: capital. Instead of risking your own money, you trade a funded account — often $50K to $150K or more — and keep a share of the profits. For traders with skill but limited funds, this changes everything.', emphasis: 'capital' },
      { text: "The barrier to entry is an evaluation, usually one or two phases where you prove you can follow rules and trade consistently. Pass, and you're funded. Some firms even offer instant funding with no eval at all." },
    ],
  },
  {
    id: 'why-fail',
    number: '02',
    title: 'Why most traders fail',
    paragraphs: [
      { text: "The pass rates tell the story — across the industry, fewer than 5–10% of traders pass evaluations. The reasons are almost always the same: overleveraging, ignoring drawdown limits, revenge trading after losses, and treating the eval like a lottery ticket instead of a process.", emphasis: 'fewer than 5–10% of traders pass evaluations' },
      { text: "Most failures aren't about strategy. They're about discipline. Traders who blow accounts almost always know the rules — they just don't follow them under pressure." },
    ],
    pullQuote: "The eval isn't testing whether you can make money. It's testing whether you can follow rules while making money.",
  },
  {
    id: 'business-model',
    number: '03',
    title: 'How prop firms actually make money',
    paragraphs: [
      { text: "This is the part most traders don't think about. Prop firms earn revenue from evaluation fees — the $50–$300+ you pay to attempt a challenge. With pass rates under 10%, the math works in their favor: most traders pay multiple times before passing, if they ever do.", emphasis: 'evaluation fees' },
      { text: "That's not a scam — it's a business model. Understanding it helps you approach the process with clear eyes. The firms that succeed long-term are the ones that also pay out consistently, because reputation drives new signups." },
    ],
  },
  {
    id: 'business-mindset',
    number: '04',
    title: 'Treat this like a business, not gambling',
    paragraphs: [
      { text: "The traders who succeed with prop firms treat their eval fee like a business expense, not a bet. They have a defined strategy, risk parameters, and daily routines. They know exactly how much they're willing to lose on any given day before they stop.", emphasis: 'business expense' },
      { text: "If you're buying eval after eval with no plan, you're not trading — you're gambling on a different game. Set a budget for how many attempts you'll take. Track your performance. If you're not ready, practice on sim until you are." },
    ],
  },
  {
    id: 'realistic-potential',
    number: '05',
    title: 'The realistic potential',
    paragraphs: [
      { text: 'Top-level prop firm traders — the ones who pass, stay funded, and withdraw consistently — can earn $3K–$15K+ per month trading one or two accounts. Some scale to multiple funded accounts across different firms and push well beyond that.', emphasis: '$3K–$15K+ per month' },
      { text: "But \"realistic\" is the key word. These are traders with months or years of screen time, a proven edge, and the emotional discipline to follow rules every single day. There's no shortcut. The potential is real, but it's earned." },
    ],
    pullQuote: "The goal isn't to get funded. The goal is to stay funded.",
  },
];

const placeholderIcons = {
  database: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
    </svg>
  ),
  quiz: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  ),
  tracker: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="m19 9-5 5-4-4-3 3" />
    </svg>
  ),
};

const placeholderTitles = {
  quiz: 'Prop Firm Quiz',
  tracker: 'News & Updates',
};

const FEATURE_CARDS = [
  {
    id: 'database',
    title: 'Prop Firm Database',
    description: 'Search and review rules, fees, drawdown types, payout policies, and eval requirements.',
    icon: placeholderIcons.database,
    path: '/firms',
    tag: 'Live',
  },
  {
    id: 'quiz',
    title: 'Prop Firm Quiz',
    description: 'Get personalized recommendations for your trading style.',
    icon: placeholderIcons.quiz,
    path: '/quiz',
    tag: 'Live',
  },
  {
    id: 'news',
    title: 'News & Updates',
    description: 'Promos, rule changes, and announcements from the prop firm world.',
    icon: placeholderIcons.tracker,
    path: '/news',
    tag: 'Coming soon',
  },
];

function QuizRoute() {
  const navigate = useNavigate();
  return <Quiz onViewFirm={(firmId) => navigate(`/firms/${firmId}`)} />;
}

function ContactRoute() {
  const navigate = useNavigate();
  return <ContactPage onBackHome={() => navigate('/')} />;
}

function NewsPageRoute() {
  return (
    <section className={styles.placeholderPage}>
      <h1 className={styles.placeholderTitle}>{placeholderTitles.tracker}</h1>
      <p className={styles.placeholderMessage}>Coming soon</p>
      <Link to="/" className={styles.quizStyleButton}>Back to Home</Link>
    </section>
  );
}

function AboutPageRoute() {
  return (
    <section className={styles.aboutPage}>
      <h1 className={styles.aboutTitle}>About PropFirmAtlas</h1>
      <div className={styles.aboutContent}>
        <p>
          PropFirmAtlas is a structured comparison platform designed to help traders evaluate futures prop firms with clarity. Instead of digging through scattered rules across a dozen different websites, the platform organizes key information — drawdown types, consistency requirements, payout rules, trading restrictions — into a consistent, standardized format. This lets traders quickly understand the real differences between firms and choose setups that match their strategy.
        </p>
        <p>
          The goal is simple: transparent data, honest comparisons, and no hidden agendas. Prop firms offer meaningful opportunities for traders who approach them the right way, but rules and risk parameters vary widely — and the details matter. By centralizing this information, PropFirmAtlas helps traders approach funded accounts with a clearer plan and a more disciplined mindset.
        </p>
        <p>
          This platform is built and maintained by an active futures trader who got tired of hunting through help centers and Discord threads to find straight answers. If you have feedback, corrections, or suggestions, reach out — this is a living project and accuracy is the priority.
        </p>
      </div>
      <Link to="/" className={styles.quizStyleButton}>Back to Home</Link>
    </section>
  );
}

function DashboardHome() {
  const navigate = useNavigate();
  const [heroSearch, setHeroSearch] = useState('');
  const heroSearchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (heroSearchRef.current && !heroSearchRef.current.contains(e.target)) setHeroSearch('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const heroSearchResults = useMemo(() => {
    if (heroSearch.length < 3) return [];
    const words = heroSearch.toLowerCase().trim().split(/\s+/);
    const results = [];
    const push = (firmName, firmId, productName, sectionTitle, matchText, matchType) => {
      results.push({ firmName, firmId, productName, sectionTitle, matchText, matchType });
    };
    for (const f of FIRMS) {
      const firmCtx = f.name.toLowerCase() + ' ';
      const matchesAll = (text) => { const t = text.toLowerCase(); return words.every(w => t.includes(w)); };
      const fInfo = (text) => push(f.name, f.id, null, null, text, 'firm-info');
      if (f.tagline && matchesAll(firmCtx + f.tagline)) fInfo(f.tagline);
      if (f.bestFor && matchesAll(firmCtx + f.bestFor)) fInfo(f.bestFor);
      if (f.drawdownType && matchesAll(firmCtx + 'Drawdown: ' + f.drawdownType)) fInfo('Drawdown: ' + f.drawdownType);
      if (f.profitSplit && matchesAll(firmCtx + 'Profit Split: ' + f.profitSplit)) fInfo('Profit Split: ' + f.profitSplit);
      if (f.maxFundedAccts && matchesAll(firmCtx + 'Max Funded: ' + f.maxFundedAccts)) fInfo('Max Funded: ' + f.maxFundedAccts);
      if (f.location && matchesAll(firmCtx + 'Location: ' + f.location)) fInfo('Location: ' + f.location);
      f.platforms.forEach(pl => { if (matchesAll(firmCtx + 'Platform: ' + pl)) fInfo('Platform: ' + pl); });
      for (const p of f.products) {
        const prodCtx = firmCtx + p.name.toLowerCase() + ' ';
        if (p.badge && matchesAll(prodCtx + p.badge)) push(f.name, f.id, p.name, null, p.badge, 'badge');
        for (const [label, value] of p.params) {
          const combined = label + ': ' + value;
          if (matchesAll(prodCtx + combined)) push(f.name, f.id, p.name, null, combined, 'param');
        }
        if (p.sections) {
          for (const sec of p.sections) {
            if (!sec.items) continue;
            for (const item of sec.items) {
              let itemText = '';
              if (typeof item === 'string') {
                itemText = item;
              } else if (item && typeof item === 'object') {
                itemText = [item.bold, ...(item.lines || [])].filter(Boolean).join(' — ');
              }
              if (itemText && matchesAll(prodCtx + itemText)) {
                push(f.name, f.id, p.name, sec.title, itemText, 'section');
              }
            }
          }
        }
      }
    }
    return results;
  }, [heroSearch]);

  const heroResultsTotal = heroSearchResults.length;
  const heroResultsCapped = heroSearchResults.slice(0, 12);

  const highlightHero = (text, term) => {
    const ws = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!ws.length) return <span style={{ color: '#8b949e' }}>{text}</span>;
    const escaped = ws.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp('(' + escaped.join('|') + ')', 'gi');
    const parts = text.split(regex);
    return (
      <span style={{ color: '#8b949e' }}>
        {parts.map((part, i) =>
          regex.test(part)
            ? <span key={i} style={{ color: '#3b82f6', background: 'rgba(59,130,246,0.15)', borderRadius: 3, padding: '0 2px' }}>{part}</span>
            : part
        )}
      </span>
    );
  };

  const handleHeroResultClick = (r) => {
    setHeroSearch('');
    navigate(`/firms/${r.firmId}`, {
      state: {
        productName: r.productName || null,
        section: r.sectionTitle || null,
      },
    });
  };

  const renderEmphasized = (para) => {
    if (!para.emphasis) return para.text;
    const parts = para.text.split(para.emphasis);
    return parts.map((part, k, arr) =>
      k < arr.length - 1
        ? <span key={k}>{part}<em className={styles.eduEmphasis}>{para.emphasis}</em></span>
        : <span key={k}>{part}</span>
    );
  };

  return (
    <>
            {/* 2-column grid: sidebar + main */}
            <div className={styles.dashboardLayout}>

              {/* LEFT sidebar — Top Firms */}
              <aside className={styles.topFirmsSidebar}>
                <h3 className={styles.topFirmsHeading}>Top Firms</h3>
                {TOP_RATED_FIRMS.map((firm) => {
                  const visitHref = getFirmVisitHref(FIRMS.find(f => f.id === firm.firmId));
                  return (
                  <div
                    key={firm.firmId}
                    className={styles.topFirmsCard}
                    onClick={() => { navigate(`/firms/${firm.firmId}`); }}
                  >
                    <img src={firm.logo} alt="" className={styles.topFirmsLogo} onError={(e) => { e.target.style.display = 'none'; }} />
                    <div className={styles.topFirmsCardBody}>
                      <span className={styles.topFirmsName}>{firm.name}</span>
                      {visitHref && (
                        <a
                          className={styles.topFirmsWebsiteLink}
                          href={visitHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={visitHref}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Visit Firm
                        </a>
                      )}
                    </div>
                    <span className={styles.topFirmsTrustpilot}>
                      <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#00B67A"/>
                      </svg>
                      <span className={styles.topFirmsTrustpilotScore}>{firm.trustpilot.toFixed(1)}</span>
                      <span className={styles.topFirmsTrustpilotLabel}>Trustpilot</span>
                    </span>
                  </div>
                  );
                })}
              </aside>

              {/* RIGHT main content */}
              <div className={styles.dashboardMain}>

                {/* Hero */}
                <section className={styles.hero}>
                  <div className={styles.heroBadge}>Top 12 Futures Prop Firms of 2026</div>
                  <h1 className={styles.heroHeadline}>
                    Navigate the world of{' '}
                    <span className={styles.heroAccent}>futures prop firms</span>
                  </h1>
                  <p className={styles.heroSubheadline}>
                    Compare rules, fees, drawdown types, payout policies, and evaluation requirements — all in one place.
                  </p>
                  <div className={styles.heroButtons}>
                    <button type="button" className={styles.quizStyleButton} onClick={() => { navigate('/firms'); }}>
                      Explore Firms
                    </button>
                    <button type="button" className={styles.secondaryButton} onClick={() => { navigate('/quiz'); }}>
                      Take the Quiz
                    </button>
                  </div>
                  <div className={styles.heroSearch} ref={heroSearchRef}>
                    <span className={styles.searchIcon} aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                    </span>
                    <input
                      type="search"
                      className={styles.searchInput}
                      placeholder="Search firms, rules, platforms, features..."
                      aria-label="Search firms, rules, platforms, features"
                      value={heroSearch}
                      onChange={e => setHeroSearch(e.target.value)}
                    />
                    {heroSearch.length >= 3 && (
                      <div className={styles.heroResultsPanel}>
                        {heroResultsCapped.length === 0 ? (
                          <div className={styles.heroResultsEmpty}>No matches found for &lsquo;{heroSearch}&rsquo;</div>
                        ) : (
                          <>
                            {heroResultsCapped.map((r, i) => (
                              <div key={i} className={styles.heroResultRow} onClick={() => handleHeroResultClick(r)}>
                                <div className={styles.heroResultBreadcrumb}>
                                  <span className={styles.heroResultFirm}>{r.firmName}</span>
                                  {r.productName && <><span className={styles.heroResultSep}>›</span><span className={styles.heroResultMuted}>{r.productName}</span></>}
                                  {r.sectionTitle && <><span className={styles.heroResultSep}>›</span><span className={styles.heroResultMuted}>{r.sectionTitle}</span></>}
                                </div>
                                <div className={styles.heroResultMatch}>{highlightHero(r.matchText, heroSearch)}</div>
                              </div>
                            ))}
                            {heroResultsTotal > 12 && (
                              <div className={styles.heroResultsFooter}>Showing 12 of {heroResultsTotal} results</div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </section>

                {/* Stats strip */}
                <div className={styles.statsStrip}>
                  <div className={styles.stat}><span className={styles.statNumber}>12</span><span className={styles.statLabel}>Firms</span></div>
                  <div className={styles.stat}><span className={styles.statNumber}>32</span><span className={styles.statLabel}>Account types</span></div>
                  <div className={styles.stat}><span className={styles.statNumber}>400+</span><span className={styles.statLabel}>Rules tracked</span></div>
                  <div className={styles.stat}><span className={styles.statNumber}>3.4–4.9</span><span className={styles.statLabel}>Trustpilot range</span></div>
                </div>

                <div className={styles.divider} />

                {/* Feature cards — 3 column */}
                <section className={styles.features}>
                  <h3 className={styles.featuresHeading}>Tools</h3>
                  <div className={styles.featuresGrid}>
                    {FEATURE_CARDS.map((feature) => (
                      <article
                        key={feature.id}
                        className={styles.card}
                        onClick={() => { navigate(feature.path); }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(feature.path); } }}
                      >
                        <div className={styles.cardIcon}>{feature.icon}</div>
                        <h2 className={styles.cardTitle}>{feature.title}</h2>
                        <p className={styles.cardDescription}>{feature.description}</p>
                        <span className={`${styles.cardTag} ${feature.tag === 'Coming soon' ? styles.cardTagSoon : ''}`}>{feature.tag}</span>
                      </article>
                    ))}
                  </div>
                </section>

                {/* Mobile-only Top Firms (hidden on desktop) */}
                <section className={styles.topFirmsMobileSection}>
                  <h3 className={styles.topFirmsMobileHeading}>Top Firms</h3>
                  <div className={styles.topFirmsMobileGrid}>
                    {TOP_RATED_FIRMS.map((firm) => {
                      const visitHref = getFirmVisitHref(FIRMS.find(f => f.id === firm.firmId));
                      return (
                      <div
                        key={firm.firmId}
                        className={styles.topFirmsMobileCard}
                        onClick={() => { navigate(`/firms/${firm.firmId}`); }}
                      >
                        <img src={firm.logo} alt="" className={styles.topFirmsMobileLogo} onError={(e) => { e.target.style.display = 'none'; }} />
                        <div className={styles.topFirmsMobileCardBody}>
                          <span className={styles.topFirmsMobileName}>{firm.name}</span>
                          {visitHref && (
                            <a
                              className={styles.topFirmsMobileWebsiteLink}
                              href={visitHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={visitHref}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Visit Firm
                            </a>
                          )}
                        </div>
                        <span className={styles.topFirmsMobileTrustpilot}>
                        <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#00B67A"/>
                        </svg>
                        <span className={styles.topFirmsMobileTrustpilotScore}>{firm.trustpilot.toFixed(1)}</span>
                        <span className={styles.topFirmsMobileTrustpilotLabel}>Trustpilot</span>
                      </span>
                      </div>
                      );
                    })}
                  </div>
                </section>

              </div>
            </div>

            {/* Education sections — outside the grid, full-width centered */}
            <section className={styles.educationSection}>
              <h3 className={styles.educationLabel}>Understanding Prop Trading</h3>
              {EDUCATION_SECTIONS.map((sec, i) => (
                <div key={sec.id}>
                  <article className={styles.eduBlock}>
                    <span className={styles.eduNumber}>{sec.number}</span>
                    <h2 className={styles.eduTitle}>{sec.title}</h2>
                    {sec.paragraphs.map((para, j) => (
                      <p key={j} className={styles.eduParagraph}>
                        {renderEmphasized(para)}
                      </p>
                    ))}
                    {sec.pullQuote && (
                      <blockquote className={styles.eduPullQuote}>
                        <p>{sec.pullQuote}</p>
                      </blockquote>
                    )}
                  </article>
                  {i < EDUCATION_SECTIONS.length - 1 && <div className={styles.eduDivider} />}
                </div>
              ))}
            </section>
    </>
  );
}

function App() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [location.pathname]);

  // iOS Safari: pinch-zoom can leave horizontal scroll / visualViewport offset so content sits in a column with empty space on the left. Snap back when zoom settles near 100%.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    let debounceId;
    const clearHorizontalDrift = () => {
      if (vv.scale > 1.02) return;
      const y = window.scrollY;
      const driftX = window.scrollX > 2 || vv.offsetLeft > 2;
      if (!driftX) return;
      window.scrollTo(0, y);
      document.documentElement.scrollLeft = 0;
      if (document.body) document.body.scrollLeft = 0;
    };

    const schedule = () => {
      window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => {
        clearHorizontalDrift();
        requestAnimationFrame(clearHorizontalDrift);
      }, 100);
    };

    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('touchend', schedule, { passive: true });

    return () => {
      window.clearTimeout(debounceId);
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('touchend', schedule);
    };
  }, []);

  const navLinkClass = ({ isActive }) =>
    `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`;

  return (
    <div className={styles.app}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.logo} style={{ textDecoration: "none" }}>
            <img src={brandMarkUrl} alt="" className={styles.logoMark} width={36} height={36} decoding="async" />
            <span className={styles.logoWordmark}>PropFirmAtlas</span>
          </Link>
          <nav className={styles.nav}>
            <NavLink end to="/" className={navLinkClass} style={{ textDecoration: "none" }}>Dashboard</NavLink>
            <NavLink to="/firms" className={navLinkClass} style={{ textDecoration: "none" }}>Prop Firms</NavLink>
            <NavLink to="/quiz" className={navLinkClass} style={{ textDecoration: "none" }}>Quiz</NavLink>
            <NavLink to="/news" className={navLinkClass} style={{ textDecoration: "none" }}>{'News & Updates'}</NavLink>
            <NavLink to="/about" className={navLinkClass} style={{ textDecoration: "none" }}>About</NavLink>
          </nav>
        </div>
      </header>

      <main className={styles.main}>
        <Routes>
          <Route path="/" element={<DashboardHome />} />
          <Route path="/firms" element={<FirmsPage />} />
          <Route path="/firms/:firmId" element={<FirmDetailRoute />} />
          <Route path="/quiz" element={<QuizRoute />} />
          <Route path="/news" element={<NewsPageRoute />} />
          <Route path="/about" element={<AboutPageRoute />} />
          <Route path="/contact" element={<ContactRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Global footer — appears on every page */}
        <footer className={styles.globalFooter}>
          <div className={styles.footerInner}>
            <div className={styles.footerBrand}>
              <div className={styles.footerLogoRow}>
                <img src={brandMarkUrl} alt="" className={styles.footerLogoMark} width={28} height={28} decoding="async" />
                <div className={styles.footerLogo}>PropFirmAtlas</div>
              </div>
              <p className={styles.footerTagline}>
                Independent futures prop firm research.<br />
                Compare rules. Make informed decisions.
              </p>
              <span className={styles.footerByline}>Built by a trader, for traders.</span>
            </div>
            <div className={styles.footerCol}>
              <h4 className={styles.footerColHeading}>Navigate</h4>
              <Link to="/" className={styles.footerLink}>Dashboard</Link>
              <Link to="/firms" className={styles.footerLink}>Prop Firms</Link>
              <Link to="/quiz" className={styles.footerLink}>Prop Firm Quiz</Link>
              <Link to="/news" className={styles.footerLink}>{'News & Updates'}</Link>
            </div>
            <div className={styles.footerCol}>
              <h4 className={styles.footerColHeading}>More</h4>
              <Link to="/contact" className={styles.footerLink}>Contact / Feedback</Link>
              <Link to="/about" className={styles.footerLink}>About PropFirmAtlas</Link>
            </div>
          </div>
          <div className={styles.footerBottom}>
            <p className={styles.footerDisclaimer}>
              PropFirmAtlas is an independent research tool. Nothing on this site constitutes financial advice. All information is provided for educational purposes only. Always do your own due diligence before purchasing any evaluation or funded account.
            </p>
            <span className={styles.footerCopyright}>&copy; 2026 PropFirmAtlas</span>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default App;
